import type { ColumnDef } from "@tanstack/react-table";
import { isEmpty } from "lodash";
import { useLayoutEffect, useMemo, useState } from "react";

import type { Transaction } from "@/apis/gql/types";
import { useTable } from "@/components/shared/data-table/useTable";
import {
  type RawFilters,
  convertRawToColumnFilters,
  useServerSideQuery,
} from "@/hooks/useQueryFilters";
import { useGetAllTransactions } from "@/queries/transactions/get-all";

export type TransactionsTodosTableInstance = ReturnType<
  typeof useTransactionsTodosTable
>;

type TransactionsTodosFilters =
  | "smbAttachmentStatus"
  | "side"
  | "status"
  | "side";

export function useTransactionsTodosTable({
  columns,
  defaultFilters,
  storageKey,
  rowSelection,
  onRowSelectionChange,
}: {
  columns: ColumnDef<Transaction>[];
  defaultFilters: RawFilters<TransactionsTodosFilters>;
  storageKey: string;
  rowSelection?: Record<string, boolean>;
  onRowSelectionChange?: (value: Record<string, boolean>) => void;
}) {
  const {
    globalSearch,
    setGlobalSearch,
    serverSideFilters,
    setFilters,
    filters,
    sorting,
    onSortingChange,
    sort,
    order,
    resetFilters,
    initialServerSideFilters,
  } = useServerSideQuery<TransactionsTodosFilters>({
    storageKey,
    defaultFilters,
    defaultSorting: {
      sort: "bookingDate",
      order: "desc",
    },
  });

  const { data: todosData, loading: todosLoading } = useGetAllTransactions({
    query: serverSideFilters,
    global: globalSearch,
    page: 0,
    limit: 9999, // hackity hack to not build pagination yet
    offset: 0,
    sort,
    order,
  });

  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >({
    counterpartyName: false,
    purpose: false,
    amount: false,
    bankConnection: false,
    hasAttachments: false,
    smbAttachmentStatus: false,
    side: false,
    invoice: false,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: we want this to be initial and not re-run
  const initialColumnFilters = useMemo(
    () => convertRawToColumnFilters(defaultFilters),
    [],
  );

  const table = useTable({
    data: todosData ?? [],
    columns,
    tableName: "transactions-todos",
    sorting,
    onSortingChange,
    getRowId: (row) => row.bankTxnId,
    columnFilters: initialColumnFilters,
    initialSorting: [{ id: "bookingDate", desc: true }],
    manualFiltering: true,
    manualSorting: true,
    manualPagination: true,
    columnVisibility,
    onColumnVisibilityChange: setColumnVisibility,
    rowSelection,
    onRowSelectionChange,
    initialServerSideFilters,
    onServerSideFiltersClear: resetFilters,
    serverSideFilters,
    globalServerSideSearch: globalSearch,
  });

  const hasSelection = useMemo(() => {
    return !isEmpty(table.table.getSelectedRowModel().rows);
  }, [table.table.getSelectedRowModel().rows]);

  useLayoutEffect(() => {
    if (hasSelection) {
      setColumnVisibility((prev) => ({
        ...prev,
        invoice: false,
      }));
    } else {
      const timeoutId = setTimeout(() => {
        setColumnVisibility((prev) => ({
          ...prev,
          invoice: true,
        }));
      }, 50);
      return () => clearTimeout(timeoutId);
    }
  }, [hasSelection]);

  return {
    ...table,
    todosLoading,
    globalSearch,
    filters,
    setFilters,
    setGlobalSearch,
  };
}
